import {
  differenceInCalendarDays,
  differenceInMonths,
  differenceInYears,
  isValid,
  parseISO,
} from 'date-fns';
import { getName } from 'i18n-iso-countries';
import '@/lib/nationalityOptions';
import { formatCurrency, formatDate } from '@/lib/formatters';

/** Trimmed string, or null when there's nothing to show. */
export function text(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const str = String(value).trim();
  return str === '' ? null : str;
}

/** Words the sentence-case pass would get wrong. */
const VALUE_OVERRIDES: Record<string, string> = {
  onsite: 'On-site',
  'on-site': 'On-site',
  domestic_partnership: 'Domestic partnership',
};

/** "full-time" → "Full-time", "MARRIED" → "Married", "pay_monthly" → "Pay monthly". */
export function humanize(value: unknown): string | null {
  const str = text(value);
  if (!str) return null;
  const lower = str.toLowerCase();
  if (VALUE_OVERRIDES[lower]) return VALUE_OVERRIDES[lower];
  const spaced = lower.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** "PK" → "Pakistan"; free text passes through unchanged. */
export function country(value: unknown): string | null {
  const str = text(value);
  if (!str) return null;
  if (/^[A-Za-z]{2}$/.test(str)) return getName(str.toUpperCase(), 'en') ?? str;
  return str;
}

function toDate(value: unknown): Date | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
}

export function date(value: unknown): string | null {
  const parsed = toDate(value);
  return parsed ? formatDate(parsed.toISOString(), 'd MMM yyyy') : null;
}

/** "28 yrs" from a date of birth. */
export function age(value: unknown): string | null {
  const parsed = toDate(value);
  if (!parsed) return null;
  const years = differenceInYears(new Date(), parsed);
  return years >= 0 ? `${years} yrs` : null;
}

/** "2 yrs 3 mos" / "5 mos" / "12 days" between a start date and now. */
export function tenure(value: unknown): string | null {
  const parsed = toDate(value);
  if (!parsed) return null;
  const now = new Date();
  const months = differenceInMonths(now, parsed);
  if (months < 0) return null;
  if (months === 0) {
    const days = differenceInCalendarDays(now, parsed);
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'yr' : 'yrs'}`);
  if (rest > 0) parts.push(`${rest} ${rest === 1 ? 'mo' : 'mos'}`);
  return parts.join(' ');
}

/** "in 12 days" / "3 days ago" / "today", relative to now. */
export function relativeDays(value: unknown): string | null {
  const parsed = toDate(value);
  if (!parsed) return null;
  const days = differenceInCalendarDays(parsed, new Date());
  if (days === 0) return 'today';
  if (days > 0) return `in ${days} ${days === 1 ? 'day' : 'days'}`;
  const ago = Math.abs(days);
  return `${ago} ${ago === 1 ? 'day' : 'days'} ago`;
}

export function isFuture(value: unknown): boolean {
  const parsed = toDate(value);
  return parsed ? differenceInCalendarDays(parsed, new Date()) > 0 : false;
}

export function money(value: unknown, currency: string | null): string | null {
  const amount = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof amount !== 'number' || Number.isNaN(amount)) return null;
  return currency ? formatCurrency(amount, currency) : amount.toLocaleString();
}

/** "•••• 4821" — enough to confirm the account without putting it on screen. */
export function maskAccount(value: unknown): string | null {
  const str = text(value);
  if (!str) return null;
  const digits = str.replace(/\s/g, '');
  return digits.length <= 4 ? digits : `•••• ${digits.slice(-4)}`;
}
